/**
 * MediaPlayerFactory
 * 
 * Factory pattern para criar players de vídeo baseado no provider
 * e dados fornecidos. Centraliza a lógica de criação de players.
 */

import React from 'react';
import NativeVideoPlayer from './NativeVideoPlayer';
import YouTubePlayer from './YouTubePlayer';
import VimeoPlayer from './VimeoPlayer';
import BunnyPlayer from './BunnyPlayer';
import IframePlayer from './IframePlayer';
import { buildEmbedUrlFromMediaMapping } from '../../utils/videoUrlBuilder';
import { validateMediaMapping } from '../../utils/providerDetector';

/**
 * Cria um componente de player baseado no MediaMapping
 * @param {Object} mediaMapping - Objeto MediaMapping do banco
 * @param {Object} playerProps - Props adicionais para o player
 * @returns {React.Component|null} Componente do player ou null se inválido
 */
export function createPlayer(mediaMapping, playerProps = {}) {
  if (!mediaMapping) {
    console.warn('⚠️ MediaPlayerFactory: MediaMapping não fornecido');
    return null;
  }

  const { provider, video_id, video_library_id, library_url } = mediaMapping;
  
  // Validar dados do MediaMapping
  const validation = validateMediaMapping(mediaMapping);
  if (!validation.isValid) {
    console.error('❌ MediaPlayerFactory: Dados inválidos:', validation.errors);
    return createErrorPlayer(validation.errors, playerProps);
  }

  console.log(`🎬 MediaPlayerFactory: Criando player para provider: ${provider}`);

  // Props comuns para todos os players
  const commonProps = {
    onTimeUpdate: playerProps.onTimeUpdate,
    onComplete: playerProps.onComplete,
    currentTime: playerProps.currentTime,
    duration: playerProps.duration,
    className: playerProps.className || "w-full h-full",
    config: playerProps.config || {}
  };

  try {
    switch (provider) {
      case 'vimeo':
        return createVimeoPlayer(mediaMapping, commonProps);

      case 'youtube':
        return createYouTubePlayer(mediaMapping, commonProps);

      case 'bunny':
        return createBunnyPlayer(mediaMapping, commonProps);

      case 'native':
        return createNativePlayer(mediaMapping, commonProps);

      case 'iframe':
        return createIframePlayer(mediaMapping, commonProps);

      default:
        console.error(`❌ MediaPlayerFactory: Provider não suportado: ${provider}`);
        return createErrorPlayer([`Provider não suportado: ${provider}`], commonProps);
    }
  } catch (error) {
    console.error('❌ MediaPlayerFactory: Erro ao criar player:', error);
    return createErrorPlayer([`Erro ao criar player: ${error.message}`], commonProps);
  }
}

/**
 * Cria um VimeoPlayer
 * @param {Object} mediaMapping - Dados do MediaMapping
 * @param {Object} commonProps - Props comuns
 * @returns {React.Component} VimeoPlayer
 */
function createVimeoPlayer(mediaMapping, commonProps) {
  const { video_id, library_url } = mediaMapping;
  
  return React.createElement(VimeoPlayer, {
    ...commonProps,
    videoId: video_id,
    url: library_url, // Fallback para compatibilidade
    key: `vimeo-${video_id}`
  });
}

/**
 * Cria um YouTubePlayer
 * @param {Object} mediaMapping - Dados do MediaMapping
 * @param {Object} commonProps - Props comuns
 * @returns {React.Component} YouTubePlayer
 */
function createYouTubePlayer(mediaMapping, commonProps) {
  const { video_id, library_url } = mediaMapping;
  
  return React.createElement(YouTubePlayer, {
    ...commonProps,
    videoId: video_id,
    url: library_url, // Fallback para compatibilidade
    key: `youtube-${video_id}`
  });
}

/**
 * Cria um BunnyPlayer
 * @param {Object} mediaMapping - Dados do MediaMapping
 * @param {Object} commonProps - Props comuns
 * @returns {React.Component} BunnyPlayer
 */
function createBunnyPlayer(mediaMapping, commonProps) {
  const { video_id, video_library_id } = mediaMapping;
  
  return React.createElement(BunnyPlayer, {
    ...commonProps,
    videoId: video_id,
    videoLibraryId: video_library_id,
    key: `bunny-${video_library_id}-${video_id}`
  });
}

/**
 * Cria um NativeVideoPlayer
 * @param {Object} mediaMapping - Dados do MediaMapping
 * @param {Object} commonProps - Props comuns
 * @returns {React.Component} NativeVideoPlayer
 */
function createNativePlayer(mediaMapping, commonProps) {
  const { library_url } = mediaMapping;
  
  return React.createElement(NativeVideoPlayer, {
    ...commonProps,
    url: library_url,
    key: `native-${library_url}`
  });
}

/**
 * Cria um IframePlayer
 * @param {Object} mediaMapping - Dados do MediaMapping
 * @param {Object} commonProps - Props comuns
 * @returns {React.Component} IframePlayer
 */
function createIframePlayer(mediaMapping, commonProps) {
  const { library_url } = mediaMapping;
  
  return React.createElement(IframePlayer, {
    ...commonProps,
    url: library_url,
    key: `iframe-${library_url}`
  });
}

/**
 * Cria um player de erro
 * @param {Array} errors - Lista de erros
 * @param {Object} commonProps - Props comuns
 * @returns {React.Component} Componente de erro
 */
function createErrorPlayer(errors, commonProps) {
  return React.createElement('div', {
    className: `${commonProps.className} bg-black flex items-center justify-center`,
    key: 'error-player'
  }, React.createElement('div', {
    className: 'text-white text-center p-4'
  }, [
    React.createElement('div', {
      key: 'icon',
      className: 'text-6xl mb-4'
    }, '⚠️'),
    React.createElement('h3', {
      key: 'title',
      className: 'text-lg font-semibold mb-2'
    }, 'Erro no Player de Vídeo'),
    React.createElement('ul', {
      key: 'errors',
      className: 'text-sm text-left max-w-md'
    }, errors.map((error, index) => 
      React.createElement('li', {
        key: index,
        className: 'mb-1'
      }, `• ${error}`)
    ))
  ]));
}

/**
 * Cria um player baseado em dados simples (para compatibilidade)
 * @param {string} url - URL do vídeo
 * @param {string} provider - Provider do vídeo
 * @param {Object} playerProps - Props adicionais
 * @returns {React.Component|null} Componente do player
 */
export function createPlayerFromUrl(url, provider, playerProps = {}) {
  if (!url || !provider) {
    console.warn('⚠️ MediaPlayerFactory: URL ou provider não fornecidos');
    return null;
  }

  // Criar MediaMapping temporário
  const tempMediaMapping = {
    provider,
    library_url: url,
    video_id: null,
    video_library_id: null
  };

  return createPlayer(tempMediaMapping, playerProps);
}

/**
 * Cria um player com fallback automático
 * @param {Object} mediaMapping - Dados do MediaMapping
 * @param {string} fallbackUrl - URL de fallback
 * @param {Object} playerProps - Props adicionais
 * @returns {React.Component|null} Componente do player
 */
export function createPlayerWithFallback(mediaMapping, fallbackUrl, playerProps = {}) {
  // Tentar criar player com MediaMapping
  if (mediaMapping && mediaMapping.provider) {
    const player = createPlayer(mediaMapping, playerProps);
    if (player) {
      return player;
    }
  }

  // Fallback para URL simples
  if (fallbackUrl) {
    console.log('🔄 MediaPlayerFactory: Usando fallback para URL:', fallbackUrl);
    return createPlayerFromUrl(fallbackUrl, 'native', playerProps);
  }

  // Último recurso: player de erro
  return createErrorPlayer(['Nenhum player disponível'], playerProps);
}

/**
 * Valida se um MediaMapping pode ser usado para criar um player
 * @param {Object} mediaMapping - Dados do MediaMapping
 * @returns {boolean} Se pode criar um player
 */
export function canCreatePlayer(mediaMapping) {
  if (!mediaMapping) {
    return false;
  }

  const validation = validateMediaMapping(mediaMapping);
  return validation.isValid;
}

/**
 * Obtém informações sobre o player que será criado
 * @param {Object} mediaMapping - Dados do MediaMapping
 * @returns {Object} Informações do player
 */
export function getPlayerInfo(mediaMapping) {
  if (!mediaMapping) {
    return { canCreate: false, provider: null, errors: ['MediaMapping não fornecido'] };
  }

  const validation = validateMediaMapping(mediaMapping);
  
  return {
    canCreate: validation.isValid,
    provider: mediaMapping.provider,
    errors: validation.errors,
    embedUrl: validation.isValid ? buildEmbedUrlFromMediaMapping(mediaMapping) : null
  };
}
